import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { type ReactNode, useMemo, type JSX } from "react";
import { useForm, useFormContext } from "react-hook-form";

import { graphql } from "@repo/graphql-types/gql";
import ArrowRight from "@repo/icons/default/ArrowRight";

import { FirstScheduleDurationField } from "@/components/onboarding/first-schedule-duration-field";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/ui/form/form";
import { InputCurrency } from "@/components/ui/input/input-currency";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useAppointmentDurationLimits } from "@/hooks/use-appointment-duration-limits";
import { useRecordFirstScheduleStop } from "@/hooks/use-first-schedule-history";
import { useGraphQL } from "@/hooks/use-graphql";
import { firstScheduleAtom } from "@/lib/atoms/first-schedule-atom";
import { AppointmentTypeCategoryEnum } from "@/lib/enums/appointment-type-category-enum";
import { AppointmentTypeModalityEnum } from "@/lib/enums/appointment-type-modality-enum";
import { FollowUpPolicyEnum } from "@/lib/enums/follow-up-policy-enum";
import {
  buildFollowUpFormSchema,
  type FollowUpFormFields,
  isFreeFollowUpPolicy,
  parseMaskedPrice,
  showsFollowUpDuration,
} from "@/lib/form-schemas/first-schedule-appointment-types-schema";
import { getFollowUpPriceSuggestion } from "@/lib/helpers/appointment-price-suggestions";
import { formatCurrency, toMaskedCurrency } from "@/lib/helpers/currency";
import {
  FIRST_SCHEDULE_STEPS,
  FOLLOW_UP_APPOINTMENT_TYPE_NAME,
  getFirstScheduleProgress,
  toDraftDuration,
} from "@/lib/helpers/first-schedule-flow";
import { requireFirstAppointmentDraft } from "@/lib/helpers/first-schedule-guards";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const FirstScheduleFollowUpQuery = graphql(/* GraphQL */ `
  query FirstScheduleFollowUpQuery(
    $codUsuario: Int!
    $name: String!
    $category: Int!
    $modality: Int!
  ) {
    followUpAppointmentTypes: usuariosCompromissos(
      where: {
        codUsuario: { _eq: $codUsuario }
        nome: { _eq: $name }
        ativo: { _eq: true }
        categoria: { _eq: $category }
        codUsuarioCompromissoModalidade: { _eq: $modality }
      }
      order_by: { codUsuarioCompromisso: desc }
      limit: 1
    ) {
      codUsuarioCompromisso
      duracao
    }
    usuarios {
      professionalData: UsuariosProfissionai {
        codTipoPoliticaRetorno
        appointmentPreferences: configuracaoAtendimentos {
          retornoGratuitoEmDias
        }
      }
    }
  }
`);

const FOLLOW_UP_POLICY_OPTIONS = [
  { value: FollowUpPolicyEnum.NoFreeFollowUp, label: "Sem retorno gratuito" },
  {
    value: FollowUpPolicyEnum.CaseByCase,
    label: "Retorno gratuito definido caso a caso pelo profissional",
  },
  { value: FollowUpPolicyEnum.FreeFollowUp, label: "Retorno gratuito" },
];

const DEFAULT_FOLLOW_UP_DURATION = "00:30";

const FirstScheduleFollowUpPage = (): JSX.Element | null => {
  const { user } = useRouteContext({ from: "__root__" });
  const form = useAtomValue(firstScheduleAtom);
  useRecordFirstScheduleStop("followUp");

  const { data, isPending } = useGraphQL(FirstScheduleFollowUpQuery, {
    codUsuario: user.codUsuario,
    name: FOLLOW_UP_APPOINTMENT_TYPE_NAME,
    category: AppointmentTypeCategoryEnum.Manual,
    modality: AppointmentTypeModalityEnum.Presencial,
  });

  if (!form.firstAppointment) return null;

  if (isPending) {
    return (
      <FollowUpLayout>
        <FullPageSpinner />
      </FollowUpLayout>
    );
  }

  const followUpType = data?.followUpAppointmentTypes[0];
  const professionalData = data?.usuarios[0]?.professionalData;

  return (
    <FollowUpLayout>
      <FollowUpForm
        currentPolicy={professionalData?.codTipoPoliticaRetorno ?? undefined}
        currentDuration={
          followUpType
            ? toDraftDuration(followUpType.duracao)
            : DEFAULT_FOLLOW_UP_DURATION
        }
        suggestedPrice={getFollowUpPriceSuggestion(form.firstAppointment.price)}
      />
    </FollowUpLayout>
  );
};

const FollowUpLayout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { flags } = useRouteContext({ from: "__root__" });

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar
          progress={getFirstScheduleProgress(
            FIRST_SCHEDULE_STEPS.followUp,
            flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"],
          )}
        />
      </FlowHeader>

      <Page className="flex flex-col pb-0 px-6" showCustomerIoContainer={false}>
        {children}
      </Page>
    </RoundedLayout>
  );
};

interface FollowUpFormProps {
  currentPolicy?: number;
  currentDuration: string;
  suggestedPrice: number;
}

const FollowUpForm = ({
  currentPolicy,
  currentDuration,
  suggestedPrice,
}: FollowUpFormProps): JSX.Element => {
  const navigate = useNavigate();
  const [draft, setDraft] = useAtom(firstScheduleAtom);
  const durationLimits = useAppointmentDurationLimits();

  const schema = useMemo(() => buildFollowUpFormSchema(durationLimits), [durationLimits]);

  const savedDraft = draft.followUp;
  const defaultPolicy =
    savedDraft?.policy ??
    FOLLOW_UP_POLICY_OPTIONS.find((option) => option.value === currentPolicy)?.value;

  const formMethods = useForm<FollowUpFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      policy: defaultPolicy,
      price: toMaskedCurrency(
        savedDraft?.policy === FollowUpPolicyEnum.NoFreeFollowUp
          ? savedDraft.price
          : suggestedPrice,
      ),
      duration: savedDraft?.duration ?? currentDuration,
    },
  });

  const selectedPolicy = formMethods.watch("policy");

  const handleSubmit = (values: FollowUpFormFields): void => {
    const isFree = isFreeFollowUpPolicy(values.policy);

    setDraft((prev) => ({
      ...prev,
      followUp: {
        policy: values.policy,
        daysToFreeFollowUp: isFree ? (prev.followUp?.daysToFreeFollowUp ?? null) : null,
        price: isFree ? 0 : parseMaskedPrice(values.price),
        duration: showsFollowUpDuration(values.policy)
          ? values.duration
          : currentDuration,
      },
    }));

    void navigate({
      to: isFree
        ? "/onboarding/first-schedule/follow-up-days"
        : "/onboarding/first-schedule/preview",
    });
  };

  return (
    <FormRoot {...formMethods}>
      <FormHandlerSubmit
        handleSubmit={handleSubmit}
        className="w-full flex-1 justify-between gap-4"
      >
        <div className="flex flex-col gap-4">
          <CardSelectTitle>Como funciona o retorno com você?</CardSelectTitle>

          <FormField
            control={formMethods.control}
            name="policy"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div>
                    <CardSelectRoot
                      name="follow-up-policy"
                      defaultValue={field.value ? String(field.value) : undefined}
                      onChange={(value) => field.onChange(Number(value))}
                    >
                      <CardSelectItemContainer>
                        {FOLLOW_UP_POLICY_OPTIONS.map((option) => (
                          <CardSelectItem
                            key={option.value}
                            value={String(option.value)}
                            className="justify-start text-left"
                          >
                            {option.label}
                          </CardSelectItem>
                        ))}
                      </CardSelectItemContainer>
                    </CardSelectRoot>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FollowUpPolicyDetails
            policy={selectedPolicy}
            suggestedPrice={suggestedPrice}
            maxDurationInMinutes={durationLimits.maxDurationInMinutes}
          />
        </div>

        <div className="sticky bottom-0 bg-white pt-4 pb-4">
          <FormSubmitButton color="secondary">
            <div className="flex flex-row items-center justify-center gap-2">
              Continuar
              <ArrowRight size={24} aria-hidden="true" />
            </div>
          </FormSubmitButton>
        </div>
      </FormHandlerSubmit>
    </FormRoot>
  );
};

interface FollowUpPolicyDetailsProps {
  policy: FollowUpPolicyEnum | undefined;
  suggestedPrice: number;
  maxDurationInMinutes: number;
}

const FollowUpPolicyDetails = ({
  policy,
  suggestedPrice,
  maxDurationInMinutes,
}: FollowUpPolicyDetailsProps): JSX.Element | null => {
  switch (policy) {
    case FollowUpPolicyEnum.CaseByCase:
      return <FollowUpDurationField maxDurationInMinutes={maxDurationInMinutes} />;
    case FollowUpPolicyEnum.NoFreeFollowUp:
      return (
        <>
          <FollowUpPriceField suggestedPrice={suggestedPrice} />
          <FollowUpDurationField maxDurationInMinutes={maxDurationInMinutes} />
        </>
      );
    default:
      return null;
  }
};

interface FollowUpPriceFieldProps {
  suggestedPrice: number;
}

const FollowUpPriceField = ({ suggestedPrice }: FollowUpPriceFieldProps): JSX.Element => {
  const { control } = useFormContext<FollowUpFormFields>();

  return (
    <FormField
      control={control}
      name="price"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <InputCurrency
              title="Valor do retorno"
              helpMessage={`Sugestão: ${formatCurrency(suggestedPrice)}, metade do valor da primeira consulta`}
              required
              {...field}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

interface FollowUpDurationFieldProps {
  maxDurationInMinutes: number;
}

const FollowUpDurationField = ({
  maxDurationInMinutes,
}: FollowUpDurationFieldProps): JSX.Element => {
  const { control } = useFormContext<FollowUpFormFields>();

  return (
    <FormField
      control={control}
      name="duration"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <FirstScheduleDurationField
              value={field.value}
              onChange={field.onChange}
              maxDurationInMinutes={maxDurationInMinutes}
              drawerTitle="Informe a duração do retorno"
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/follow-up")({
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"]) {
      redirect({ to: "/onboarding/first-schedule/success", throw: true });
    }
    requireFirstAppointmentDraft();
  },
  component: FirstScheduleFollowUpPage,
});
